import invariant from 'tiny-invariant';
import * as yup from 'yup';

import { Logger } from '@mopla/logger';

import { fromBase64UrlSafe, toBase64UrlSafe } from './base64UrlSafe';
import {
	ITrueLayerCallbackData,
	ITrueLayerCallbackDataRaw,
	ITrueLayerState,
} from './types';

/**
 * Yup schema for TrueLayer state validation
 */
const trueLayerStateSchema = yup.object({
	restorePage: yup.string().required('restorePage is required'),
	query: yup.string().optional(),
	restoreState: yup.string().optional(),
});

/**
 * Yup schema for TrueLayer callback URL parameters validation
 * Accepts either success (code) or error (error) parameter, but not both
 */
const trueLayerCallbackSchema = yup
	.object({
		code: yup
			.string()
			.min(3, 'TrueLayer callback "code" parameter too short')
			.nullable(),
		error: yup.string().nullable(),
		state: yup
			.string()
			.min(1, 'TrueLayer callback "state" parameter too short')
			.required('TrueLayer callback missing required "state" parameter'),
	})
	.test(
		'code-or-error',
		'TrueLayer callback must have either "code" (success) or "error" (failure) parameter',
		(value) => Boolean(value.code || value.error)
	);

/**
 * Encodes TrueLayer state to URL-safe Base64
 * Prevents TrueLayer from incorrectly parsing & characters in query strings
 */
function encodeTrueLayerState(state: ITrueLayerState): string {
	const jsonStr = JSON.stringify(state);
	return toBase64UrlSafe(jsonStr);
}

/**
 * Decodes TrueLayer state from URL-safe Base64
 * First decodes Base64, then parses JSON
 */
function decodeTrueLayerState(stateParam: string): ITrueLayerState {
	try {
		const jsonStr = fromBase64UrlSafe(stateParam);
		return JSON.parse(jsonStr);
	} catch (error) {
		const errorMessage = `Failed to decode TrueLayer state: ${stateParam}`;

		Logger.getInstance('payment').error(errorMessage, error, {
			stateParam,
		});

		throw new Error(errorMessage);
	}
}

/**
 * Extracts error parameter from TrueLayer callback URL/URLSearchParams
 * Handles TrueLayer bug where URLs have double "?" breaking normal query parsing
 *
 * Example malformed URL: "http://localhost:3100/?truelayer_redirect=true?error=access_denied&state=..."
 *
 * @returns Error code if present (e.g., 'access_denied'), null otherwise
 */
export function extractErrorFromTrueLayerUrl(
	url: URL | URLSearchParams
): string | null {
	const params = url instanceof URL ? url.searchParams : url;

	/**
	 * Try to extract error from URL string directly due to TrueLayer bug
	 * params.toString() returns URL-encoded string, so decode it first
	 */
	const paramsDecoded = decodeURIComponent(params.toString());
	const errorMatch = paramsDecoded.match(/[?&]error=([^&]+)/);
	const errorFromUrl = errorMatch ? errorMatch[1] : null;

	/** Fallback to normal searchParams.get() if regex didn't find anything */
	return errorFromUrl || params.get('error') || null;
}

/**
 * Parses TrueLayer callback URL and extracts code/error + state
 * Used by redirect processors (both web and native)
 *
 * Success case: Returns code (authorization code) + state
 * Error case: Returns error (error code like 'access_denied', 'invalid_request', etc.) + state
 *
 * @throws Error if both code and error are missing, or if state is missing or invalid
 */
export function parseTrueLayerCallback(
	url: URL | URLSearchParams
): ITrueLayerCallbackData {
	const params = url instanceof URL ? url.searchParams : url;

	/** Extract error from TrueLayer */
	const trueLayerError = extractErrorFromTrueLayerUrl(url);

	const callbackParamsRaw: ITrueLayerCallbackDataRaw = {
		code: params.get('code'),
		error: trueLayerError,
		state: params.get('state'),
	};

	/** Collect validation error if schema validation fails */
	let validationError: Error | null = null;
	try {
		trueLayerCallbackSchema.validateSync(callbackParamsRaw);
	} catch (error: unknown) {
		validationError = error as yup.ValidationError;
	}

	/**
	 * Decode and validate state.
	 * If there's no state or the state is invalid - this error is critical - we cannot restore anything after the TrueLayer redirect
	 * */
	invariant(callbackParamsRaw.state, 'TrueLayer state is missing');

	const state = decodeTrueLayerState(callbackParamsRaw.state);

	try {
		trueLayerStateSchema.validateSync(state);
	} catch {
		throw new Error('Invalid TrueLayer state structure');
	}

	const callbackParamsParsed: ITrueLayerCallbackData = {
		state,
		code: callbackParamsRaw.code,
		error: null,
	};

	/** Prioritize TrueLayer error over validation error */
	if (trueLayerError) {
		/** TrueLayer error is a string error code, wrap it in Error with descriptive message */
		callbackParamsParsed.error = new Error(
			`TrueLayer authorization error: ${trueLayerError}`
		);
	} else if (validationError) {
		/** Validation error is already an Error object */
		callbackParamsParsed.error = validationError;
	}

	return callbackParamsParsed;
}

/**
 * Builds TrueLayer authentication URL with encoded state parameters
 * Pure function with no environment dependencies
 *
 * 1) Building URL for TrueLayer OAuth flow
 * `baseUrl` templates are stored in passenger environment:
 *  * Web: `environment.trueLayerAuthLinkWeb`
 *  * Native: `environment.trueLayerAuthLinkNative`
 *
 * TrueLayer is not currently used in the Payment app
 *
 * Important: `baseUrl` already contains the `redirect_uri` query parameter, which points to the Mopla index page with additional query parameters.
 * Production examples:
 *  * https://auth.truelayer.com/?
 *        redirect_uri=https://app.mopla.solutions/?truelayer_redirect=true&....
 *        &...other_params
 *  * https://auth.truelayer.com/?
 *        redirect_uri=mopla.passenger://auth-callback?truelayer_redirect=true&.....
 *
 * Therefore, we additionally add restorePage & restoreQuery parameters to the state query parameter.
 * This parameter will be added during the redirect to TrueLayer authorization, and subsequently received back when returning to Mopla.
 */
export function buildTrueLayerAuthUrl(
	baseUrl: string,
	restorePage: string,
	restoreQuery?: string
): string {
	const url = new URL(baseUrl);

	const stateObject: ITrueLayerState = {
		restorePage,
		query: restoreQuery,
	};

	/** State is encoded into URL-safe base64 format */
	const encodedState = encodeTrueLayerState(stateObject);

	url.searchParams.set('state', encodedState);

	return url.toString();
}
